import { toast } from 'react-toastify';

import User from 'classes/User';
import { checkPermission } from 'components/PermissionHide/checkPermission';
import { clearUser, setUser } from 'errors/errorHandler';
import { setHotjarUser } from 'hotjar';
import type { HotjarUser } from 'hotjar';
import { setIntercomUser } from 'intercom';
import type { IAccessPolicyPermissionsAction } from 'interfaces/IAccessPolicies';
import { initializePendo } from 'pendo';
import type { PendoConfig } from 'pendo';
import * as Rx from 'rxjs';
import { catchError, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { ACCOUNTS_ENV, ACCOUNTS_TOKEN, DEV_PERSIST_STORAGE_KEYS, IS_DEV_SESSION_PERSIST } from 'settings';
import Cookies from 'universal-cookie';
import type { IAccess, IAccountInfo, IAuthAccounts, ISwitchAccount } from 'v2/interfaces/account';
import type { IApiResponse } from 'v2/interfaces/api';
import type { BlacklistEntry } from 'v2/interfaces/blacklist';
import { BlackBeltOrMore } from 'v2/interfaces/elite';
import type { FeatureAccessData } from 'v2/interfaces/featureAccess';
import type { IHasUserBetaModule } from 'v2/interfaces/shopping';
import { userStore } from 'v2/store/User';

import apiService from './api';
import type { ApiService } from './api';
import chatBotService from './chatBot';
import tokenService from './token';
import type { TokenService } from './token';

const VERTICAL_COOKIE = 'vertical-switchaccount';

export class EduzzAccountsService {
  private initialized = false;
  private accesses$ = new Rx.BehaviorSubject<IAccess[]>([]);
  private isLogged$ = new Rx.BehaviorSubject<boolean>(false);
  private permissions$ = new Rx.BehaviorSubject<IAccessPolicyPermissionsAction>(null);
  private newRefundPermission$ = new Rx.BehaviorSubject<boolean>(false);
  private user$ = new Rx.BehaviorSubject<User>(null);
  private originalUser$ = new Rx.BehaviorSubject<User>(null);
  private loadingAccount$ = new Rx.BehaviorSubject<boolean>(true);
  private hasSession$ = new Rx.BehaviorSubject<boolean>(false);
  private hasInit$ = new Rx.BehaviorSubject<boolean>(false);
  private betaModules$ = new Rx.BehaviorSubject<string[]>([]);
  private loadingBetaModules$ = new Rx.BehaviorSubject<boolean>(true);
  private tours$ = new Rx.BehaviorSubject<string[]>([]);
  private loadingTours$ = new Rx.BehaviorSubject<boolean>(true);
  private fingerprint$ = new Rx.BehaviorSubject<string>(null);
  private cookies = new Cookies();

  constructor(
    private tokenService: TokenService,
    private apiService: ApiService
  ) {
    this.tryRestoreDevSessionFromStorage();
  }

  public login(registerScreen?: boolean) {
    return new Promise(resolve => {
      return (window as any).Eduzz.Accounts.login(ACCOUNTS_TOKEN, {
        logo: 'https://orbita.eduzz.com/img/logo-myeduzz-login.png',
        env: ACCOUNTS_ENV,
        register: registerScreen ?? false,
isPartnerCreate: true,
        isPartnerLogin: true
      }).subscribe(resolve);
    });
  }

  public initialize(registerScreen?: boolean) {
    this.initialized = true;
    this.hasInit$.next(true);
    Rx.from(this.login(registerScreen))
      .pipe(
        switchMap(token => this.apiService.post('/auth/accounts', { token })),
        catchError(err => {
          toast.error(err);

          this.hasInit$.next(false);

          setTimeout(() => {
            this.logout();
          }, 5000);

          return Rx.of({});
        })
      )
      .subscribe((response: IApiResponse<IAuthAccounts>) => {
        this.clearLocalStorage();

        if (response.data) {
          const { ssid, token, refreshToken, customer, fingerprint } = response.data;

          this.tokenService.setToken(token);
          this.tokenService.setRefreshToken(refreshToken);
          this.loadAccesses(ssid);
          this.tokenService.setAccountsSsid(ssid);
          this.setFingerprint(fingerprint);

          if (customer?.account_id) this.tokenService.setAccountsId(customer.account_id);
          if (customer?.qualification_key) this.tokenService.setQualificationKey(customer.qualification_key);
        }
      });
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public updateUrl() {
    const currentUrl = `${window.location.origin}${window.location.pathname}`;
    const oldUrl = window.location.href;
    const newUrl = new URL(oldUrl);
    const params = new URLSearchParams(newUrl.search.slice(1));
    params.delete('accounts_token');
    params.delete('accounts_login_token');
    const newParams = params.toString() ? `?${params.toString()}` : '';
    window.history.pushState(null, null, `${currentUrl}${newParams}`);
  }

  public changeAccount(email: string) {
    this.loadingAccount$.next(true);

    this.user$.next(null);
    this.originalUser$.next(null);
    this.permissions$.next(null);
    this.isLogged$.next(false);
    this.apiService
      .post('/access-control/switchaccount', { email })
      .subscribe(({ data }: IApiResponse<ISwitchAccount>) => {
        this.setVerticalCookie(email);

        if (IS_DEV_SESSION_PERSIST) {
          localStorage.setItem(DEV_PERSIST_STORAGE_KEYS.selectedAccountEmail, email);
        }

        this.tokenService.setToken(data.token);
        this.isLogged$.next(true);
        this.loadAccountDetails();
        this.loadPermissions();
        this.loadBetaModules();
        this.loadTours();
        this.loadingAccount$.next(false);
      });
  }

  public loadAccesses(ssid: string): void {
    this.apiService.get('/access-control/accesses').subscribe((response: IApiResponse<IAccess[]>) => {
      const { data: accesses } = response;
      const onlyOneAccess = accesses?.length === 1;

      if (onlyOneAccess) {
        this.loadAccountDetails();
        this.loadPermissions();
        this.loadBetaModules();
        this.loadTours();
        this.isLogged$.next(true);
      }

      this.setAccesses(accesses);
      this.loadingAccount$.next(false);

      const lastEmailSelected = this.getSelectedAccountPreference();
      const multipleAccesses = accesses?.length > 1;

      const isSameAccount = accesses.some(a => a.email === lastEmailSelected);

      if (lastEmailSelected && multipleAccesses && isSameAccount) this.changeAccount(lastEmailSelected);

      localStorage.setItem('ssid', ssid);

      this.updateUrl();
    });
  }

  public loadBetaModules(): void {
    this.loadingBetaModules$.next(true);
    this.apiService
      .get('/auth/beta-modules')
      .pipe(
        map(response => [...(response.data ?? []), 'toolbar']),
        tap(data => {
          this.betaModules$.next(data);
        }),
        finalize(() => this.loadingBetaModules$.next(false))
      )
      .subscribe();
  }

  public getBetaModules(): Rx.Observable<string[]> {
    return this.betaModules$;
  }

  public getTours(): Rx.Observable<string[]> {
    return this.tours$;
  }

  public verifyUserHasBetaModule(module: string): boolean {
    return this.betaModules$.value.includes(module);
  }

  public verifyUserHasBetaModuleByProducerId(producerId: number, module: string): Rx.Observable<IHasUserBetaModule> {
    return this.apiService
      .get('auth/is-beta-customer-in-module', {
        beta_customer_id: producerId,
        module
      })
      .pipe(tap(response => response.data.data));
  }

  public getAccesses(): Rx.Observable<IAccess[]> {
    return this.accesses$;
  }

  public setAccesses(accesses: IAccess[]): void {
    this.accesses$.next(accesses);
  }

  public getIsLogged(): Rx.Observable<boolean> {
    return this.isLogged$;
  }

  public getUser(): Rx.BehaviorSubject<User> {
    return this.user$;
  }

  public getLoadingAccount(): Rx.Observable<boolean> {
    return this.loadingAccount$;
  }

  public getLoadingBetaModules(): Rx.Observable<boolean> {
    return this.loadingBetaModules$;
  }

  public getNewRefundPermission(): Rx.BehaviorSubject<boolean> {
    return this.newRefundPermission$;
  }

  public setNewRefundPermission(user: any): void {
    this.apiService
      .get(`/access-control/${user.id}/permission-new-refund-service?is_producer=${user.producer}`)
      .pipe(map(({ data }) => data))
      .subscribe(data => {
        this.newRefundPermission$.next(data.can_access_new_refund);
      });
  }

  public getUserVertical() {
    return Rx.combineLatest([
      this.getUser(),
      this.getOriginalUser(),
      this.getPermissions(),
      this.getBetaModules(),
      this.getNewRefundPermission()
    ]).pipe(
      map(([user, originalUser, permissions, betas, newRefundPermission]) => {
        function getUserTag(user: User) {
          if (user.is_clube_black) return 'unity';
          if (user.acceptence_terms) return 'pro';
          return undefined;
        }

        const isAccessPolicy = user.id !== originalUser.id;

        return {
          id: user.id,
          email: user.email,
          document: user.document,
          name: user.name,
          avatar: user.photo,
          tag: getUserTag(user),
          isClubeBlack: user.is_clube_black,
          betas,
          isAccessPolicy,
          originalUserId: isAccessPolicy ? originalUser.id : null,
          belt: user.current_elite_level.name,
          isSupport: user.is_support,
          supportId: user.is_support ? user.support_id : null,
          is: {
            producer: user.producer,
            affiliate: user.affiliate,
            coproducer: user.coproducer,
            customer: user.beta_customer,
            partner: user.general_term,
            agency: user.is_agency
          },
          permissions: {
            owned: permissions.owned,
            actions: permissions.actions,
            isAbleToUseRefundService: newRefundPermission
          },
          terms: {
            general: user.general_term,
            acceptence: user.acceptence_terms
          }
        } as Omit<MyEduzzCurrentUser, 'checkPermission'>;
      })
    );
  }

  public getOriginalUser(): Rx.Observable<User> {
    return this.originalUser$;
  }

  public getFingerprint(): Rx.Observable<string> {
    return this.fingerprint$;
  }

  public setUser(user: IAccountInfo): void {
    const { customer, original_customer } = user;
    const { id, email, full_name, current_elite_level } = customer;
    const belt = this.getBeltById(current_elite_level.id);

    setUser(user.customer);

    chatBotService.register(customer.id);

    setHotjarUser({
      id,
      email,
      name: full_name,
      belt,
      isAccessControl: customer.id === original_customer.id
    } as HotjarUser);

    const newUser = new User(customer);
    const newOriginalUser = new User(original_customer);

    setIntercomUser(user, belt);
    userStore.getState().setUser(newUser);
    userStore.getState().setOriginalUser(newOriginalUser);

    this.user$.next(newUser);
    this.originalUser$.next(newOriginalUser);

    this.setNewRefundPermission(newUser);
    this.setupPendo(user);
  }

  public loadAccountDetails(): void {
    this.apiService.get('/auth/details').subscribe(({ data }) => this.setUser(data));
  }

  public loadTours(): void {
    this.loadingTours$.next(true);
    this.apiService.get('/profile/tours').subscribe(({ data }) => {
      this.tours$.next(data);
      this.loadingTours$.next(false);
    });
  }

  public loadPermissions(): void {
    this.apiService.get('/access-control/permissions').subscribe(({ data }) => this.setPermissions(data));
  }

  public getPermissions(): Rx.Observable<IAccessPolicyPermissionsAction> {
    return this.permissions$;
  }

  public setPermissions(permissions: any): void {
    const permissionMap = {
      owned: permissions.owned,
      actions: Object.keys(permissions.accesses).reduce((acc, key) => {
        acc.push(...permissions.accesses[key].actions.map((a: { fullname: any }) => a.fullname));
        return acc;
      }, [])
    } as IAccessPolicyPermissionsAction;

    this.permissions$.next(permissionMap);
  }

  public logout() {
    this.setVerticalCookie(null);
    this.clearLocalStorage();
    clearUser();
    this.loadingAccount$.next(true);
    this.user$.next(null);
    this.originalUser$.next(null);
    this.permissions$.next(null);
    this.isLogged$.next(false);
    this.tokenService.clearToken();
    window.history.pushState(null, null, '/');
    (window as any).Eduzz.Accounts.logout({ env: ACCOUNTS_ENV });
    // window.history.pushState(null, null, null);
  }

  public hasPermission(action: string): boolean {
    return checkPermission(this.permissions$.value, action);
  }

  public isInit(): Rx.Observable<boolean> {
    return this.hasInit$;
  }

  public hasSession(): Rx.Observable<boolean> {
    return this.hasSession$;
  }

  public saveTourAction(tour: string) {
    this.tours$.next([...this.tours$.value, tour]);
    return this.apiService.post('/profile/tours', { tour });
  }

  public getLoadingTours() {
    return this.loadingTours$;
  }

  public isBlackBeltOrMore() {
    const userEliteLevelId = this.user$.value?.current_elite_level?.id;
    const idsQualified = Object.values(BlackBeltOrMore);
    return idsQualified.includes(userEliteLevelId);
  }

  public getBeltById(id: number): string {
    const belts: Record<number, string> = {
      100: 'Red Belt',
      101: 'Orange Belt',
      102: 'Green Belt',
      103: 'Black Belt',
      104: 'Golden Belt',
      109: 'Black Belt D2',
      110: 'Black Belt D3',
      111: 'Black Belt D4',
      112: 'Black Belt D5',
      113: 'Black Belt D6',
      114: 'Black Belt D7',
      115: 'Black Belt D8',
      116: 'Black Belt D9',
      300: 'Sensei',
      302: 'Golden Belt D20',
      303: 'Golden Belt D30',
      304: 'Golden Belt D40',
      305: 'Golden Belt D50',
      306: 'Golden Belt D60',
      307: 'Golden Belt D70',
      308: 'Golden Belt D80',
      309: 'Golden Belt D90'
    };

    return belts[id] || null;
  }

  public userHasBelt(beltId: number[]) {
    return beltId.includes(this.user$.value?.current_elite_level?.id);
  }

  public checkQualificationRedirect(accountId: string) {
    return this.apiService
      .get(`/qualification/check-redirect/${accountId}`)
      .toPromise()
      .then(res => res.data);
  }

  public checkPendingQualification(eduzzId: number) {
    return this.apiService
      .get(`/qualification/check-pending/${eduzzId}`)
      .toPromise()
      .then(res => res.data);
  }

  public getFeatureAccessData(): Promise<FeatureAccessData> {
    return this.apiService
      .get('/customers/feature-access')
      .pipe(map(response => response.data ?? []))
      .toPromise();
  }

  public getBetaFeatures(): Promise<string[]> {
    return this.apiService
      .get('/beta-features')
      .pipe(map(response => response.data ?? []))
      .toPromise();
  }

  public getBlacklistEntries(): Promise<BlacklistEntry[]> {
    return this.apiService
      .get('/customers/blacklist')
      .pipe(map(response => response.data ?? []))
      .toPromise();
  }

  private clearLocalStorage(): void {
    localStorage.removeItem('ssid');
    localStorage.removeItem('id');

    if (IS_DEV_SESSION_PERSIST) {
      localStorage.removeItem(DEV_PERSIST_STORAGE_KEYS.selectedAccountEmail);
    }
  }

  /** Preferência de conta (cookie em prod; localStorage em dev — localhost costuma falhar com cookie de domínio). */
  private getSelectedAccountPreference(): string | null {
    if (IS_DEV_SESSION_PERSIST) {
      const persisted = localStorage.getItem(DEV_PERSIST_STORAGE_KEYS.selectedAccountEmail);

      if (persisted) {
        return persisted;
      }
    }

    return this.getVerticalCookie();
  }

  private getVerticalCookie(): string {
    return this.cookies.get(VERTICAL_COOKIE) as string;
  }

  private setVerticalCookie(email: string | null) {
    const host = window.location.hostname.split('.');
    host.shift();

    if (!email) {
      this.cookies.remove(VERTICAL_COOKIE, { path: '/', domain: `.${host.join('.')}` });
      return;
    }

    this.cookies.set(VERTICAL_COOKIE, email, { path: '/', domain: `.${host.join('.')}` });
  }

  private setFingerprint(fingerprint: string) {
    this.fingerprint$.next(fingerprint);
  }

  private setupPendo(user: IAccountInfo) {
    const { customer, original_customer } = user;
    const { id, email, full_name, current_elite_level } = customer;

    if (original_customer.is_support || customer.is_support || !original_customer.acceptence_terms) {
      return;
    }

    const belt = this.getBeltById(current_elite_level.id);

    const account: PendoConfig['account'] = {
      id: original_customer.id,
      fullName: original_customer.full_name,
      email: original_customer.email,
      type: original_customer.type,
      alreadyTransfered: original_customer.already_transfered,
      belt
    };

    const visitor: PendoConfig['visitor'] = {
      id,
      fullName: full_name,
      email
    };

    initializePendo(account, visitor);
  }

  /**
   * Dev: após hidratar JWT no TokenService, recarrega acessos/conta sem passar pelo Accounts.login.
   */
  private tryRestoreDevSessionFromStorage(): void {
    if (!IS_DEV_SESSION_PERSIST) {
      return;
    }

    const token = this.tokenService.getTokenSnapshot();

    if (!token || !this.tokenService.decode(token)) {
      return;
    }

    const ssid =
      this.tokenService.getAccountsSsidSnapshot() ||
      localStorage.getItem(DEV_PERSIST_STORAGE_KEYS.accountsSsid) ||
      localStorage.getItem('ssid');

    if (!ssid) {
      return;
    }

    this.tokenService
      .setAccountsSsid(ssid)
      .pipe(take(1))
      .subscribe(() => {
        this.loadingAccount$.next(true);
        this.loadAccesses(ssid);
      });

    this.initialized = true;
  }
}

const eduzzAccountsService = new EduzzAccountsService(tokenService, apiService);
export default eduzzAccountsService;
